import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const DOC_TYPES = {
  contract: { label: 'Contrat de vente', icon: 'FileText', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  title: { label: 'Titre de propriété', icon: 'Home', color: 'bg-green-100 text-green-700 border-green-200' },
  evidence: { label: 'Pièce justificative', icon: 'Shield', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  escrow_agreement: { label: 'Accord de séquestre', icon: 'Lock', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  other: { label: 'Autre document', icon: 'File', color: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const PARTICIPANT_ROLES = {
  buyer: { label: 'Acheteur', color: 'bg-blue-500' },
  seller: { label: 'Vendeur', color: 'bg-green-500' },
  agent: { label: 'Agent', color: 'bg-amber-500' },
  notary: { label: 'Notaire', color: 'bg-purple-500' },
  admin: { label: 'Admin', color: 'bg-red-500' },
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ─── Active Editors Indicator ─────────────────────────────────────────────────
const ActiveEditorsBar = ({ editors, currentUserId }) => {
  if (!editors?.length) return null;
  const others = editors?.filter(e => e?.user_id !== currentUserId);
  if (!others?.length) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs">
      <div className="flex -space-x-1">
        {others?.slice(0, 3)?.map((editor, idx) => (
          <div
            key={idx}
            className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold border border-white ${
              PARTICIPANT_ROLES?.[editor?.role]?.color || 'bg-gray-400'
            }`}
            title={editor?.name}
          >
            {editor?.name?.[0]?.toUpperCase() || '?'}
          </div>
        ))}
      </div>
      <span className="text-amber-700 font-medium">
        {others?.length === 1
          ? `${others?.[0]?.name} est en train d'éditer...`
          : `${others?.length} participants éditent...`}
      </span>
      <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse ml-auto" />
    </div>
  );
};

// ─── Document Card ────────────────────────────────────────────────────────────
const DocumentCard = ({ doc, onEdit, onView, currentUserId }) => {
  const cfg = DOC_TYPES?.[doc?.doc_type] || DOC_TYPES?.other;
  const isEditing = doc?.active_editors?.some(e => e?.user_id !== currentUserId);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all group">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${cfg?.color}`}>
          <Icon name={cfg?.icon} size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 truncate">{doc?.title}</p>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg?.color}`}>
              {cfg?.label}
            </span>
            {isEditing && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                En cours d'édition
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            Modifié par {doc?.last_edited_by_name || 'Inconnu'} · {formatDate(doc?.updated_at)}
          </p>
          {doc?.version > 1 && (
            <p className="text-xs text-gray-400">Version {doc?.version}</p>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onView(doc)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title="Voir"
          >
            <Icon name="Eye" size={14} />
          </button>
          <button
            onClick={() => onEdit(doc)}
            className="p-1.5 rounded-lg hover:bg-amber-100 text-gray-400 hover:text-amber-600 transition-colors"
            title="Éditer"
          >
            <Icon name="Edit3" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Document Editor Modal ────────────────────────────────────────────────────
const DocumentEditorModal = ({ doc, conversationId, onClose, onSave, currentUser, userProfile }) => {
  const [content, setContent] = useState(doc?.content || '');
  const [saving, setSaving] = useState(false);
  const [activeEditors, setActiveEditors] = useState([]);
  const presenceChannelRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Broadcast presence
  useEffect(() => {
    if (!doc?.id || !currentUser) return;

    const channel = supabase?.channel(`doc-presence-${doc?.id}`, {
      config: { presence: { key: currentUser?.id } },
    });

    channel?.on('presence', { event: 'sync' }, () => {
        const state = channel?.presenceState();
        const editors = Object.values(state)?.flat()?.map(p => ({
          user_id: p?.user_id,
          name: p?.name,
          role: p?.role,
        }));
        setActiveEditors(editors);
      })?.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel?.track({
            user_id: currentUser?.id,
            name: userProfile?.full_name || currentUser?.email,
            role: userProfile?.role || 'user',
          });
        }
      });

    presenceChannelRef.current = channel;

    return () => {
      channel?.untrack();
      supabase?.removeChannel(channel);
    };
  }, [doc?.id, currentUser, userProfile]);

  // Listen for remote content changes
  useEffect(() => {
    if (!doc?.id) return;
    const channel = supabase?.channel(`doc-changes-${doc?.id}`)?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'transaction_documents',
        filter: `id=eq.${doc?.id}`,
      }, (payload) => {
        // Only update if not currently editing (to avoid overwriting local changes)
        if (document.activeElement?.tagName !== 'TEXTAREA') {
          setContent(payload?.new?.content || '');
        }
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [doc?.id]);

  const handleContentChange = (e) => {
    setContent(e?.target?.value);
    // Auto-save with debounce
    if (saveTimeoutRef?.current) clearTimeout(saveTimeoutRef?.current);
    saveTimeoutRef.current = setTimeout(() => {
      handleSave(e?.target?.value, true);
    }, 2000);
  };

  const handleSave = async (contentToSave = content, isAutoSave = false) => {
    if (!doc?.id) return;
    if (!isAutoSave) setSaving(true);
    try {
      await supabase?.from('transaction_documents')?.update({
          content: contentToSave,
          updated_at: new Date()?.toISOString(),
          last_edited_by: currentUser?.id,
          last_edited_by_name: userProfile?.full_name || currentUser?.email,
          version: (doc?.version || 1) + (isAutoSave ? 0 : 1),
        })?.eq('id', doc?.id);
      if (!isAutoSave) onSave();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      if (!isAutoSave) setSaving(false);
    }
  };

  const others = activeEditors?.filter(e => e?.user_id !== currentUser?.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${DOC_TYPES?.[doc?.doc_type]?.color || DOC_TYPES?.other?.color}`}>
              <Icon name={DOC_TYPES?.[doc?.doc_type]?.icon || 'File'} size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{doc?.title}</h3>
              <p className="text-xs text-gray-400">Édition collaborative · Version {doc?.version || 1}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {others?.length > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                <span className="text-xs text-amber-700 font-medium">{others?.length} co-éditeur{others?.length > 1 ? 's' : ''}</span>
              </div>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Active editors */}
        {others?.length > 0 && (
          <div className="px-6 pt-3">
            <ActiveEditorsBar editors={activeEditors} currentUserId={currentUser?.id} />
          </div>
        )}

        {/* Editor */}
        <div className="flex-1 overflow-hidden p-6">
          <textarea
            value={content}
            onChange={handleContentChange}
            placeholder="Rédigez le contenu du document ici... Les modifications sont synchronisées en temps réel avec tous les participants."
            className="w-full h-full min-h-[300px] px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 transition-all font-mono"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            <Icon name="Save" size={12} className="inline mr-1" />
            Sauvegarde automatique activée
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              Fermer
            </button>
            <button
              onClick={() => handleSave(content, false)}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
            >
              {saving ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Save" size={14} />}
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── New Document Modal ───────────────────────────────────────────────────────
const NewDocumentModal = ({ conversationId, onClose, onCreate, currentUser, userProfile }) => {
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('contract');
  const [content, setContent] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!title?.trim() || !conversationId) return;
    setCreating(true);
    try {
      const { data, error } = await supabase?.from('transaction_documents')?.insert({
          conversation_id: conversationId,
          title: title?.trim(),
          doc_type: docType,
          content: content?.trim(),
          created_by: currentUser?.id,
          last_edited_by: currentUser?.id,
          last_edited_by_name: userProfile?.full_name || currentUser?.email,
          version: 1,
        })?.select()?.single();
      if (error) throw error;
      onCreate(data);
    } catch (err) {
      console.error('Create doc error:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Nouveau document de transaction</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Titre du document *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
              placeholder="Ex: Contrat de vente - Appartement Cocody"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Type de document</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(DOC_TYPES)?.map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setDocType(key)}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-medium transition-all ${
                    docType === key ? `${cfg?.color} shadow-sm` : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <Icon name={cfg?.icon} size={14} />
                  {cfg?.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Contenu initial (optionnel)</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e?.target?.value)}
              placeholder="Commencez à rédiger le document..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 transition-all"
            />
          </div>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={!title?.trim() || creating}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
          >
            {creating ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Plus" size={14} />}
            Créer
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── View Document Modal ──────────────────────────────────────────────────────
const ViewDocumentModal = ({ doc, onClose, onEdit }) => {
  const cfg = DOC_TYPES?.[doc?.doc_type] || DOC_TYPES?.other;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${cfg?.color}`}>
              <Icon name={cfg?.icon} size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{doc?.title}</h3>
              <p className="text-xs text-gray-400">{cfg?.label} · Version {doc?.version || 1}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {doc?.content ? (
            <pre className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{doc?.content}</pre>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Icon name="FileText" size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">Document vide</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Dernière modification : {doc?.last_edited_by_name} · {formatDate(doc?.updated_at)}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              Fermer
            </button>
            <button
              onClick={() => { onClose(); onEdit(doc); }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all"
            >
              <Icon name="Edit3" size={14} />
              Éditer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CollaborativeDocumentEditor = ({ conversationId, escrowTransactionId }) => {
  const { user, userProfile } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [showNewDocModal, setShowNewDocModal] = useState(false);
  const [syncStatus, setSyncStatus] = useState('connected');

  const loadDocuments = useCallback(async () => {
    if (!conversationId) return;
    try {
      const { data, error } = await supabase?.from('transaction_documents')?.select('*')?.eq('conversation_id', conversationId)?.order('updated_at', { ascending: false });
      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Realtime subscription for document changes
  useEffect(() => {
    if (!conversationId) return;
    setSyncStatus('connecting');

    const channel = supabase?.channel(`transaction-docs-${conversationId}`)?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'transaction_documents',
        filter: `conversation_id=eq.${conversationId}`,
      }, (payload) => {
        setSyncStatus('synced');
        setTimeout(() => setSyncStatus('connected'), 2000);

        if (payload?.eventType === 'INSERT') {
          setDocuments(prev => [payload?.new, ...prev]);
        } else if (payload?.eventType === 'UPDATE') {
          setDocuments(prev => prev?.map(d => d?.id === payload?.new?.id ? { ...d, ...payload?.new } : d));
        } else if (payload?.eventType === 'DELETE') {
          setDocuments(prev => prev?.filter(d => d?.id !== payload?.old?.id));
        }
      })?.subscribe((status) => {
        if (status === 'SUBSCRIBED') setSyncStatus('connected');
        if (status === 'CHANNEL_ERROR') setSyncStatus('error');
      });

    return () => supabase?.removeChannel(channel);
  }, [conversationId]);

  const handleDocCreated = (doc) => {
    setDocuments(prev => [doc, ...prev]);
    setShowNewDocModal(false);
    setEditingDoc(doc);
  };

  const handleSaveDoc = () => {
    loadDocuments();
    setEditingDoc(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon name="Files" size={16} className="text-amber-500" />
          <span className="text-sm font-semibold text-gray-900">Documents de transaction</span>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{documents?.length}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Sync status */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${
            syncStatus === 'connected' ? 'bg-green-50 text-green-700 border-green-200' :
            syncStatus === 'synced' ? 'bg-blue-50 text-blue-700 border-blue-200' :
            syncStatus === 'connecting'? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${
              syncStatus === 'connected' ? 'bg-green-500' :
              syncStatus === 'synced' ? 'bg-blue-500 animate-pulse' :
              syncStatus === 'connecting'? 'bg-amber-500 animate-pulse' : 'bg-red-500'
            }`} />
            {syncStatus === 'connected' ? 'Sync' :
             syncStatus === 'synced' ? 'Mis à jour' :
             syncStatus === 'connecting' ? 'Connexion...' : 'Erreur'}
          </div>
          <button
            onClick={() => setShowNewDocModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-xs font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm"
          >
            <Icon name="Plus" size={12} />
            Nouveau
          </button>
        </div>
      </div>

      {/* Document list */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : documents?.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Icon name="Files" size={24} className="text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-500">Aucun document</p>
            <p className="text-xs text-gray-400 mt-1">Créez le premier document de cette transaction</p>
            <button
              onClick={() => setShowNewDocModal(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-xs font-semibold hover:from-amber-600 hover:to-orange-600 transition-all mx-auto"
            >
              <Icon name="Plus" size={13} />
              Créer un document
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {documents?.map(doc => (
              <DocumentCard
                key={doc?.id}
                doc={doc}
                onEdit={setEditingDoc}
                onView={setViewingDoc}
                currentUserId={user?.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showNewDocModal && (
        <NewDocumentModal
          conversationId={conversationId}
          onClose={() => setShowNewDocModal(false)}
          onCreate={handleDocCreated}
          currentUser={user}
          userProfile={userProfile}
        />
      )}
      {editingDoc && (
        <DocumentEditorModal
          doc={editingDoc}
          conversationId={conversationId}
          onClose={() => setEditingDoc(null)}
          onSave={handleSaveDoc}
          currentUser={user}
          userProfile={userProfile}
        />
      )}
      {viewingDoc && (
        <ViewDocumentModal
          doc={viewingDoc}
          onClose={() => setViewingDoc(null)}
          onEdit={setEditingDoc}
        />
      )}
    </div>
  );
};

export default CollaborativeDocumentEditor;
